import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, Mail, Heart } from 'lucide-react';
import axios from 'axios';
import { GoogleReviewsBadge } from '../GoogleReviewsBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch settings');
      }
    };
    fetchSettings();
  }, []);

  return (
    <footer className="bg-shrimp-beige/50 border-t border-shrimp-border/50" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand */}
          <div>
            <h3 className="font-display text-xl font-semibold text-shrimp-dark mb-1">
              Just Keep Shrimping
            </h3>
            <p className="text-xs text-shrimp-text mb-3">
              Owned and Operated by: {settings?.seller_first_name || (!settings ? '' : 'Sanjin')} {settings?.seller_surname || (!settings ? '' : 'Blazinic')}
            </p>
            <p className="text-shrimp-text text-sm leading-relaxed mb-4">
              {settings?.footer_description || (!settings ? '\u00A0' : 'A hobbyist blog dedicated to sharing our journey of breeding beautiful Neocaridina shrimp.')}
            </p>
            <div className="flex items-center gap-4">
              {settings?.facebook_url && (
                <a 
                  href={settings.facebook_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-shrimp-text hover:text-shrimp-green transition-colors"
                  data-testid="footer-facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {settings?.instagram_url && (
                <a 
                  href={settings.instagram_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-shrimp-text hover:text-shrimp-green transition-colors"
                  data-testid="footer-instagram"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {settings?.youtube_url && (
                <a 
                  href={settings.youtube_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-shrimp-text hover:text-red-500 transition-colors"
                  data-testid="footer-youtube"
                >
                  <Youtube className="w-5 h-5" />
                </a>
              )}
              {settings?.contact_email && (
                <a 
                  href={`mailto:${settings.contact_email}`}
                  className="text-shrimp-text hover:text-shrimp-green transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-lg font-semibold text-shrimp-dark mb-4">
              Quick Links
            </h4>
            <nav className="flex flex-col gap-2">
              <Link to="/" className="footer-link text-sm" data-testid="footer-link-home">Home</Link>
              <Link to="/blog" className="footer-link text-sm" data-testid="footer-link-blog">Blog</Link>
              <Link to="/gallery" className="footer-link text-sm" data-testid="footer-link-gallery">Gallery</Link>
              <Link to="/shrimp-room" className="footer-link text-sm" data-testid="footer-link-shrimp-room">Shrimp Room</Link>
              <Link to="/equipment" className="footer-link text-sm" data-testid="footer-link-equipment">Equipment</Link>
              <Link to="/about" className="footer-link text-sm" data-testid="footer-link-about">About Us</Link>
              <Link to="/terms" className="footer-link text-sm" data-testid="footer-link-terms">Terms & Conditions</Link>
              <Link to="/contact" className="footer-link text-sm" data-testid="footer-link-contact">Contact</Link>
            </nav>
          </div>

          {/* About Us */}
          <div>
            <h4 className="font-display text-lg font-semibold text-shrimp-dark mb-4">
              The Science Behind It
            </h4>
            <div className="bg-shrimp-beige/50 border-l-4 border-shrimp-green rounded-lg p-4" data-testid="genetics-footer">
              <p className="text-shrimp-dark text-sm font-medium mb-2">
                🧬 {settings?.footer_science_title || (!settings ? '\u00A0' : 'Geneticists at Heart')}
              </p>
              <p className="text-shrimp-text text-xs leading-relaxed">
                {settings?.footer_science_text || (!settings ? '\u00A0' : "We're both geneticists bringing scientific understanding to our shrimp breeding hobby. Exploring colour genetics, selective breeding, and the fascinating world of Neocaridina.")}
              </p>
            </div>
          </div>
        </div>

        {settings?.google_reviews_show_footer && settings?.google_reviews_url && (
          <div className="mt-10 flex justify-center" data-testid="footer-google-reviews">
            <GoogleReviewsBadge settings={settings} />
          </div>
        )}

        {/* Copyright */}
        <div className="mt-12 pt-8 border-t border-shrimp-border/50 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="text-center sm:text-left">
            <p className="text-shrimp-text text-sm flex items-center gap-1">
              © {currentYear} Just Keep Shrimping. Made with <Heart className="w-4 h-4 text-shrimp-orange" /> for our tiny aquatic friends.
            </p>
            <p className="text-shrimp-text text-xs">
              Owned and Operated by: Sanjin Blazinic
            </p>
          </div>
          <Link 
            to="/login" 
            className="text-shrimp-border hover:text-shrimp-text text-xs transition-colors"
            data-testid="footer-admin-link"
          >
            Admin
          </Link>
        </div>
      </div>
    </footer>
  );
};
